import { put, takeEvery, take, select, call, all } from 'redux-saga/effects';
import profile from '@benniehealth/core-js/store/profile';
import { setUser as setSentryUser, captureException } from '@sentry/react';
import {
  disable as analyticsDisable,
  identify as analyticsIdentify,
  setGroup as analyticsSetGroup,
  register as analyticsRegister,
  setProfile as analyticsSetProfile,
} from '@app/helpers/analytics';

import {
  checkCompanyAccessRequest,
  checkCompanyAccessSuccess,
  checkCompanyAccessError,
} from '@app/store/companyAccesses';

import {
  fetchClientAttributesRequest,
  fetchClientAttributesSuccess,
  fetchClientAttributesError,
} from '@app/store/clientAttributes';

import {
  fetchDocumentsRequest,
  fetchDocumentsSuccess,
  fetchDocumentsError,
} from '@app/store/enrollmentDocuments';

import {
  saveSessionError,
  saveSessionRequest,
  saveSessionSuccess,
} from '@app/store/sessions';

import * as umsApi from '@app/api/ums';
import { handleBCARedirect } from '@app/helpers/environment';

import { getAnonymousLaunchDarklyUserContext } from '@app/helpers/launchDarklyAnonymousContext';

import {
  fetchInitialDataRequest,
  fetchInitialDataSuccess,
  fetchInitialDataError,
} from './index';

const toLaunchDarklyContextKey = value => String(value ?? '').trim();

function* runFetchUMSUser() {
  try {
    const user = yield call(umsApi.fetchUMSUser);

    if (handleBCARedirect(user)) {
      return null;
    }

    return user;
  } catch (error) {
    captureException(error, {
      tags: { context: 'ums_user_fetch' },
      extra: {
        operation: 'fetchUMSUser',
        saga: 'runFetchUMSUser',
      },
    });
    return null;
  }
}

function* runFetchProfile() {
  yield put(profile.actions.fetchProfileRequest());
  yield take([
    profile.actions.fetchProfileSuccess.type,
    profile.actions.fetchProfileError.type,
  ]);
}

function* runFetchClientAttributes() {
  yield put(fetchClientAttributesRequest());
  yield take([fetchClientAttributesSuccess, fetchClientAttributesError]);
}

function* runFetchApplicationStatus() {
  yield put(profile.actions.fetchApplicationStatusRequest());
  yield take([
    profile.actions.fetchApplicationStatusSuccess.type,
    profile.actions.fetchApplicationStatusError.type,
  ]);
}

function* runCheckCompanyAccesses() {
  yield put(checkCompanyAccessRequest());
  yield take([checkCompanyAccessSuccess, checkCompanyAccessError]);
}

function* runFetchDocuments() {
  yield put(fetchDocumentsRequest());
  yield take([fetchDocumentsSuccess, fetchDocumentsError]);
}

function* runSaveSession() {
  yield put(saveSessionRequest());
  yield take([saveSessionSuccess, saveSessionError]);
}

const setFeatureFlagUser = async (
  identify,
  { profileId, companyId, company, userId, isImpersonationSession, email },
) => {
  const userKey = toLaunchDarklyContextKey(profileId);
  const companyKey = toLaunchDarklyContextKey(companyId);

  const runIdentify = context =>
    new Promise((resolve, reject) => {
      identify(context, null, (error, flags) => {
        if (error) {
          reject(error);
        } else {
          resolve(flags);
        }
      });
    });

  if (!userKey || !companyKey) {
    captureException(
      new Error(
        'LaunchDarkly identify: missing user or company context key; using anonymous context',
      ),
      {
        tags: { section: 'LaunchDarkly', action: 'setFeatureFlagUser' },
        extra: {
          hasUserKey: Boolean(userKey),
          hasCompanyKey: Boolean(companyKey),
          profileId,
          companyId,
          isImpersonationSession,
        },
      },
    );
    return runIdentify(getAnonymousLaunchDarklyUserContext());
  }

  const userContext = {
    kind: 'user',
    key: userKey,
    company,
    bennieId: userId,
    ...(email && { email }),
  };

  const companyContext = {
    kind: 'company',
    key: companyKey,
    name: company,
  };

  const impersonationContext = {
    kind: 'impersonation',
    key: isImpersonationSession ? 'true' : 'false',
    value: isImpersonationSession,
  };
  const platformContext = {
    kind: 'platform',
    key: 'web',
    value: 'web',
  };

  const multiContext = {
    kind: 'multi',
    user: userContext,
    company: companyContext,
    impersonation: impersonationContext,
    platform: platformContext,
  };

  return runIdentify(multiContext);
};

const setAnalyticsUser = (
  disableAnalytics,
  { id, company, subsidiaryId, userId, roleType },
) => {
  if (disableAnalytics) {
    analyticsDisable();
  } else {
    analyticsIdentify(id);
    analyticsSetGroup('company', company);
    analyticsRegister({ subsidiaryId, roleType });
    analyticsSetProfile({ subsidiaryId, userId });
  }
};

export function* fetchInitialData({ payload }) {
  try {
    yield runFetchUMSUser();

    yield all([runFetchApplicationStatus(), runCheckCompanyAccesses()]);

    const [shouldLogout, isSuperAdmin, isImpersonationSession] = yield all([
      select(state => state.profile.userApplicationStatus.shouldLogout),
      select(state => state.companyAccesses.isSuperAdmin),
      select(state => state.companyAccesses.isImpersonationSession),
    ]);

    if (isSuperAdmin) {
      // don't do anything else if Super Admin. This will be false once the impersonation seession
      // starts, at which point this whole thing will run again.
      return;
    }

    if (shouldLogout && !isSuperAdmin && !isImpersonationSession) {
      payload.navigate(`/logout`);
      return;
    }

    yield all([
      runFetchProfile(),
      runFetchDocuments(),
      runFetchClientAttributes(),
    ]);

    const profileId = yield select(profile.selectors.profileIdSelector);
    const companyId = yield select(profile.selectors.getCompanyId);
    const subsidiaryId = yield select(profile.selectors.getSubsidiaryId);
    const roleType = yield select(profile.selectors.getRoleType);
    const company = yield select(
      state => state.companyAccesses.currentCompanySubdomain,
    );
    const email = yield select(profile.selectors.emailSelector);
    const disableAnalytics = isSuperAdmin || isImpersonationSession;
    const { userId, identify } = payload;

    yield all([
      call(setFeatureFlagUser, identify, {
        profileId,
        companyId,
        company,
        userId,
        isImpersonationSession,
        email,
      }),
      call(setSentryUser, { id: profileId }),
      call(setAnalyticsUser, disableAnalytics, {
        id: profileId,
        company,
        subsidiaryId,
        userId,
        roleType,
      }),
    ]);

    if (!isImpersonationSession) {
      yield runSaveSession();
    }

    // TODO we could probably do some prefetching of images here like we do on native.

    yield put(fetchInitialDataSuccess());
  } catch (error) {
    captureException(error, {
      tags: { context: 'initial_data_fetch' },
      extra: {
        saga: 'fetchInitialData',
        errorMessage: error?.message,
      },
    });
    // Must not throw: InitialDataGate holds a loader until this dispatch settles
    // initialDataAlreadyFetched, so a non-Error rejection here would strand the user.
    yield put(fetchInitialDataError({ error: error?.message }));
  }
}

export function* fetchInitialDataWatcher() {
  yield takeEvery(fetchInitialDataRequest, fetchInitialData);
}
